import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, FormControl, Autocomplete,
  Box, Tab, Tabs, IconButton, Typography, useTheme, Slide,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { useTenders } from '../../hooks/TenderContext';
import { FIELDS, STATUSES, WIN_STATUSES, PRIORITIES } from '../../utils/constants';
import dayjs from 'dayjs';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const EMPTY_TENDER = {
  tender_number: '', client: '', subject: '', field: '',
  status: 'בבחינה', win_method: '', manager: '', handler: '',
  bidders_meeting: '', clarification_date: '', submission_date: '',
  notes: '', win_status: '', priority: '', decision_notes: '',
  win_notes: '',
};

const TenderForm = () => {
  const theme = useTheme();
  const { isFormOpen, closeForm, editingTender, createTender, updateTender } = useTenders();
  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState(EMPTY_TENDER);
  const [errors, setErrors] = useState({});

  const isEdit = Boolean(editingTender);

  useEffect(() => {
    if (editingTender) {
      setFormData({
        ...EMPTY_TENDER,
        ...editingTender,
        bidders_meeting: editingTender.bidders_meeting ? dayjs(editingTender.bidders_meeting).format('YYYY-MM-DD') : '',
        clarification_date: editingTender.clarification_date ? dayjs(editingTender.clarification_date).format('YYYY-MM-DD') : '',
        submission_date: editingTender.submission_date ? dayjs(editingTender.submission_date).format('YYYY-MM-DD') : '',
      });
    } else {
      setFormData(EMPTY_TENDER);
    }
    setTab(0);
    setErrors({});
  }, [editingTender, isFormOpen]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleAutoChange = (field) => (_, value) => {
    setFormData(prev => ({ ...prev, [field]: value || '' }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.client.trim()) newErrors.client = 'שדה חובה';
    if (!formData.subject.trim()) newErrors.subject = 'שדה חובה';
    if (!formData.field) newErrors.field = 'שדה חובה';
    if (!formData.status) newErrors.status = 'שדה חובה';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setTab(0);
      return;
    }

    const data = {
      ...formData,
      bidders_meeting: formData.bidders_meeting || null,
      clarification_date: formData.clarification_date || null,
      submission_date: formData.submission_date || null,
    };

    try {
      if (isEdit) {
        await updateTender(editingTender.rowKey, data);
      } else {
        await createTender(data);
      }
      closeForm();
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const renderFreeSoloSelect = (field, label, options, required = false) => (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={formData[field] || ''}
      onChange={handleAutoChange(field)}
      onInputChange={(_, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${label}${required ? ' *' : ''}`}
          error={Boolean(errors[field])}
          helperText={errors[field]}
        />
      )}
    />
  );

  return (
    <Dialog
      open={isFormOpen}
      onClose={closeForm}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 1,
      }}>
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? 'עריכת מכרז' : 'מכרז חדש'}
        </Typography>
        <IconButton onClick={closeForm} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab label="פרטים בסיסיים" />
          <Tab label="תאריכים" />
          <Tab label="תוצאות" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 3, minHeight: 350 }}>
        {/* Tab 1: Basic Details */}
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="מספר מכרז"
              size="small"
              fullWidth
              value={formData.tender_number}
              onChange={handleChange('tender_number')}
            />
            <TextField
              label="מזמין *"
              size="small"
              fullWidth
              value={formData.client}
              onChange={handleChange('client')}
              error={Boolean(errors.client)}
              helperText={errors.client}
            />
            <TextField
              label="נושא המכרז *"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={formData.subject}
              onChange={handleChange('subject')}
              error={Boolean(errors.subject)}
              helperText={errors.subject}
            />
            {/* תחום - with free text option */}
            {renderFreeSoloSelect('field', 'תחום', FIELDS, true)}
            {/* סטטוס - with free text option */}
            {renderFreeSoloSelect('status', 'סטטוס', STATUSES, true)}
            {/* אופן זכייה - free text */}
            <TextField
              label="אופן זכייה"
              size="small"
              fullWidth
              value={formData.win_method}
              onChange={handleChange('win_method')}
              placeholder='לדוגמה: 60% איכות 40% מחיר'
            />
            <TextField
              label="מנהל"
              size="small"
              fullWidth
              value={formData.manager}
              onChange={handleChange('manager')}
            />
            <TextField
              label="גורם מטפל"
              size="small"
              fullWidth
              value={formData.handler}
              onChange={handleChange('handler')}
            />
          </Box>
        )}

        {/* Tab 2: Dates */}
        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                שאלות הבהרה
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={formData.clarification_date}
                onChange={handleChange('clarification_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                מפגש מציעים
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={formData.bidders_meeting}
                onChange={handleChange('bidders_meeting')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                מועד הגשה
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={formData.submission_date}
                onChange={handleChange('submission_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        )}

        {/* Tab 3: Results */}
        {tab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* תוצאה - with free text + allow empty */}
            {renderFreeSoloSelect('win_status', 'תוצאה', WIN_STATUSES.filter(Boolean))}
            {renderFreeSoloSelect('priority', 'תעדוף', PRIORITIES)}
            <TextField
              label="החלטה / הערות"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.decision_notes || ''}
              onChange={handleChange('decision_notes')}
            />
            <TextField
              label="הערות זכייה"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={formData.win_notes || ''}
              onChange={handleChange('win_notes')}
            />
            <TextField
              label="הערות כלליות"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <Button onClick={closeForm} color="inherit">ביטול</Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          sx={{ px: 4 }}
        >
          {isEdit ? 'עדכון' : 'יצירה'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenderForm;
